import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useEntitlement } from "@/lib/entitlement";
import { logError } from "@/lib/log";
import { useFavorites } from "@/lib/favorites";
import BreedCard from "@/components/BreedCard";
import { Heart, Sparkles, Trash2, Clock, Crown } from "lucide-react";

export default function Saved() {
  const { unlocked } = useEntitlement();
  const { favorites, refresh } = useFavorites();
  const [quizzes, setQuizzes] = useState([]);

  const loadQuizzes = async () => {
    try {
      const r = await axios.get(`${API}/me/quiz-results`);
      setQuizzes(r.data);
    } catch (error) {
      logError("Failed to load saved quizzes:", error);
    }
  };

  useEffect(() => { loadQuizzes(); refresh(); }, []); // eslint-disable-line

  const deleteQuiz = async (id) => {
    await axios.delete(`${API}/me/quiz-results/${id}`);
    loadQuizzes();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <div className="mb-8">
        <p className="text-primary font-semibold uppercase tracking-wider text-xs">Your library</p>
        <h1 className="font-display font-bold text-4xl md:text-5xl text-accent mt-2">
          Saved matches
        </h1>
        <p className="text-muted-foreground mt-2">
          Saved on this device — no account needed. 🐾
        </p>
      </div>

      {/* Plan status banner */}
      {unlocked ? (
        <div
          data-testid="unlocked-banner"
          className="mb-10 rounded-3xl bg-accent text-accent-foreground p-5 md:p-6 flex flex-wrap items-center gap-4 warm-shadow"
        >
          <span className="w-11 h-11 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
            <Crown size={18} />
          </span>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-bold text-lg">Fetchright Unlocked</div>
            <div className="text-sm text-accent-foreground/70">
              Every match, every cost detail, unlimited saves. Thank you 💛
            </div>
          </div>
        </div>
      ) : (
        <div
          data-testid="locked-banner"
          className="mb-10 rounded-3xl border-2 border-dashed border-primary/40 bg-primary/5 p-5 md:p-6 flex flex-wrap items-center gap-4"
        >
          <span className="w-11 h-11 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0">
            <Heart size={18} />
          </span>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-bold text-lg text-accent">
              You have {favorites.length} saved breed{favorites.length === 1 ? "" : "s"}
            </div>
            <div className="text-sm text-muted-foreground">
              Unlock every quiz match, priority advisor and full cost detail for a one-time $2.
            </div>
          </div>
          <Link
            data-testid="upgrade-cta-btn"
            to="/upgrade"
            className="pill-btn inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold"
          >
            <Sparkles size={14} /> Unlock — $2
          </Link>
        </div>
      )}

      {/* Favorite breeds */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-6">
          <Heart size={20} className="text-primary" />
          <h2 className="font-display font-bold text-2xl text-accent">Favorite breeds</h2>
          <span className="text-sm text-muted-foreground">({favorites.length})</span>
        </div>
        {favorites.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-border p-10 text-center">
            <p className="text-muted-foreground">
              Tap the heart on any breed card to save it here.
            </p>
            <Link
              to="/breeds"
              className="pill-btn mt-5 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold"
            >
              Browse breeds
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((f) => <BreedCard key={f.breed_id} breed={f.breed} />)}
          </div>
        )}
      </section>

      {/* Saved quizzes */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <Sparkles size={20} className="text-primary" />
          <h2 className="font-display font-bold text-2xl text-accent">Saved quiz results</h2>
          <span className="text-sm text-muted-foreground">({quizzes.length})</span>
        </div>
        {quizzes.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-border p-10 text-center">
            <p className="text-muted-foreground">No saved quizzes yet. Complete the quiz and hit &ldquo;Save results.&rdquo;</p>
            <Link
              to="/quiz"
              className="pill-btn mt-5 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold"
            >
              Take the quiz
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {quizzes.map((q) => (
              <div key={q.id} data-testid={`saved-quiz-${q.id}`} className="rounded-3xl bg-card border border-border/60 warm-shadow p-6 md:p-8">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <div className="font-display font-bold text-lg text-accent">
                      {q.label || `Quiz result from ${new Date(q.created_at).toLocaleDateString()}`}
                    </div>
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                      <Clock size={12} /> {new Date(q.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button
                    data-testid={`delete-quiz-${q.id}`}
                    onClick={() => deleteQuiz(q.id)}
                    className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {q.top_matches.slice(0, 3).map((m) => (
                    <Link
                      key={m.breed.id}
                      to={`/breeds/${m.breed.id}`}
                      className="flex items-center gap-3 rounded-2xl border border-border/60 p-3 hover:border-primary transition-colors"
                    >
                      <img src={m.breed.image} alt={m.breed.name} className="w-14 h-14 rounded-xl object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-accent truncate">{m.breed.name}</div>
                        <div className="text-xs text-muted-foreground">{m.compatibility}% match</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
